import { createContext, useContext, useEffect, useMemo, useState } from "react";
import * as React from "react";
import axios from "axios";
import {
  Branch,
  BranchView,
  useLocationConfiguration,
} from "./LocationConfiguration";
// import { ApplicationData } from "@tlc/ls2pac/app/ApplicationData";

export interface BranchLimitContextType {
  branchLimits: BranchLimit[];
  selectedBranchIds: BranchLimit["identifier"][];
  selectedBranches: BranchLimit[];
  isConfigLimited: boolean;
  displayBranchLimitControl: boolean;
  displayInCopiesBranchIds: BranchLimit["identifier"][];
  displayInBranchLimitsIds: BranchLimit["identifier"][];
  /**
   * not including shared branches
   */
  singleCopiesBranchId: BranchLimit["identifier"] | null;
  /**
   * @deprecated
   */
  visibleBranches: BranchLimit[];
  /**
   * @deprecated
   */
  visibleBranchIds: BranchLimit["identifier"][];
  displayInPickupLocationIds: BranchLimit["identifier"][];
  /**
   * selected branches or config limited branches
   */
  limitedBranchIds: BranchLimit["identifier"][];
  configLimitedBranches: BranchView[];
  configLimitedBranchIds: BranchLimit["identifier"][];
  updateSelectedBranches: (
    selectedBranchIds: BranchLimit["identifier"][],
  ) => void;
}

// Create the Branch Limit Context
export const BranchLimitContext = createContext<BranchLimitContextType>(null);

export const useBranchLimits = () => {
  const context = useContext(BranchLimitContext);

  if (!context) {
    throw new Error(
      "useBranchLimits must be used within a BranchLimitProvider",
    );
  }

  return context;
};

// This replaces branchLimitsUtil in ApplicationData
// eventually when the whole app is in React this can merge with BranchLimitProvider
export const BranchLimitsHolder = (() => {
  let isConfigLimited: Readonly<boolean> | null = null;
  let branchLimits: Readonly<BranchLimit>[] | null = null;
  let pendingGetBranchLimiting: Promise<BranchLimiting> = null;
  // TODO validate that branches selected are not hidden and are in branchLimits
  let selectedBranchIds: BranchLimit["identifier"][] = [];
  const subscribers: ((event: {
    selectedBranchIds: Readonly<BranchLimit["identifier"][]>;
  }) => void)[] = []; // Array to hold subscriber functions

  const getSelectedBranchIds = (): Readonly<BranchLimit["identifier"][]> => {
    return [...selectedBranchIds];
  };

  const setSelectedBranchIds = (
    limitedBranches: BranchLimit["identifier"][],
  ) => {
    selectedBranchIds = [...limitedBranches];
    subscribers.forEach((subscriber) =>
      subscriber({
        selectedBranchIds,
      }),
    );
  };

  const subscribe = (
    subscriber: (event: {
      selectedBranchIds: Readonly<BranchLimit["identifier"][]>;
    }) => void,
  ) => {
    // Add the subscriber function to the list of subscribers
    subscribers.push(subscriber);

    subscriber({ selectedBranchIds: [...selectedBranchIds] });

    // Return an unsubscribe function
    return () => {
      const index = subscribers.indexOf(subscriber);
      if (index !== -1) {
        subscribers.splice(index, 1); // Remove the subscriber when unsubscribed
      }
    };
  };

  /**
   * static data once fetched
   */
  const fetchBranchLimits = async () => {
    if (branchLimits?.length) {
      return {
        isConfigLimited,
        branchLimits,
      };
    }

    if (pendingGetBranchLimiting) {
      return pendingGetBranchLimiting;
    }

    pendingGetBranchLimiting = axios
      .get<BranchLimiting>("/branch/branchLimits")
      .then(({ data }) => {
        isConfigLimited = data.isConfigLimited;
        branchLimits = data.branchLimits;
        return {
          isConfigLimited,
          branchLimits,
        };
      });

    return pendingGetBranchLimiting;
  };

  return {
    setSelectedBranchIds,
    getSelectedBranchIds,
    subscribe,
    fetchBranchLimits,
    getCurrentBranchLimits: () => {
      return branchLimits;
    },
    getCurrentIsConfigLimited: () => {
      return isConfigLimited;
    },
  };
})();

export function getDisplayedBranchLimitIds(branchLimits: BranchLimit[]) {
  return (branchLimits || [])
    .filter((branch) => branch.displayInBranchLimits)
    .filter((branch) => branch.hidden === false)
    .map(({ identifier }) => identifier);
}

export interface BranchLimit {
  displayName: string;
  longDescription: string;
  identifier: string;
  /**
   * @deprecated selected flag is not used by LS2Pac UI
   */
  selected: boolean;
  /**
   * same as Branch.shared
   * set to true when "Share this location" is checked in LS2Preferences
   * if true, automatically added to branchFilters of /search endpoint regardless of displayInCopies, displayInBranchLimits, or displayInPickupLocation value.
   * Also included by default when branchFilters is empty
   */
  hidden: boolean;
  /**
   * set in LS2Admin > LS Settings > General > Location Display Manager
   * unlike displayInBranchLimits and displayInPickupLocation, ignores hidden
   * oddly enough, can be disabled despite being enabled in displayInBranchLimits
   * Used for all copies, Item level holds, 'Check Other Locations' link
   */
  displayInCopies: boolean;
  /**
   * set in LS2Admin > LS Settings > General > Location Display Manager
   * overriden by hidden
   */
  displayInBranchLimits: boolean;
  /**
   * set in LS2Admin > LS Settings > General > Location Display Manager
   * overriden by hidden
   * used by pickup location selector
   */
  displayInPickupLocation: boolean;
}

export interface BranchLimiting {
  isConfigLimited: boolean;
  branchLimits: BranchLimit[];
}

// Branch Limit Context Provider Component
export const BranchLimitProvider = ({ children }: React.PropsWithChildren) => {
  const [branchLimits, setBranchLimits] = useState<BranchLimit[]>(
    BranchLimitsHolder.getCurrentBranchLimits(),
  );
  const [isConfigLimited, setIsConfigLimited] = useState<boolean>(
    BranchLimitsHolder.getCurrentIsConfigLimited(),
  );
  const {
    locationConfiguration: { filterBranches } = { filterBranches: null },
  } = useLocationConfiguration();
  const [selectedBranchIds, setSelectedBranchIds] = useState<
    BranchLimit["identifier"][]
  >([...BranchLimitsHolder.getSelectedBranchIds()]);
  const selectedBranches = useMemo(() => {
    if (!branchLimits?.length) {
      return [];
    }

    return branchLimits.filter((b) => selectedBranchIds.includes(b.identifier));
  }, [branchLimits, selectedBranchIds]);

  const configLimitedBranches = React.useMemo(
    () => (filterBranches || []).filter((branch) => branch.shared === false),
    [filterBranches],
  );
  const configLimitedBranchIds = React.useMemo(
    () => configLimitedBranches.map((branch) => branch.identifier),
    [configLimitedBranches],
  );

  const limitedBranchIds = useMemo(() => {
    if (selectedBranchIds?.length) {
      return selectedBranchIds;
    }

    return configLimitedBranches.map((branch) => branch.identifier);
  }, [isConfigLimited, configLimitedBranches, selectedBranchIds]);

  const visibleBranches = React.useMemo(
    () =>
      (branchLimits || []).filter(
        (branch) => branch.hidden === false && branch.displayInCopies,
      ),
    [branchLimits],
  );

  const visibleBranchIds = React.useMemo(
    () => visibleBranches.map(({ identifier }) => identifier),
    [visibleBranches],
  );

  const displayInCopiesBranchIds = React.useMemo(
    () =>
      (branchLimits || [])
        .filter((branch) => branch.displayInCopies)
        .map(({ identifier }) => identifier),
    [branchLimits],
  );

  const displayInPickupLocationIds = React.useMemo(
    () =>
      (branchLimits || [])
        .filter((branch) => branch.displayInPickupLocation)
        .filter((branch) => branch.hidden === false)
        .map(({ identifier }) => identifier),
    [branchLimits],
  );

  const displayInBranchLimitsIds = React.useMemo(
    () => getDisplayedBranchLimitIds(branchLimits),
    [branchLimits],
  );

  const singleCopiesBranchId = React.useMemo(() => {
    // original logic used displayInCopies and !hidden
    // kgray indicated no use face for displayInBranchLimits && !displayInCopies
    if (displayInBranchLimitsIds?.length === 1) {
      return displayInBranchLimitsIds[0];
    }
    return null;
  }, [displayInBranchLimitsIds]);

  const displayBranchLimitControl = React.useMemo(
    () =>
      displayInBranchLimitsIds.length > 1 && configLimitedBranches.length !== 1,
    [displayInBranchLimitsIds, configLimitedBranches],
  );

  const updateSelectedBranches = (
    limitedBranches: BranchLimit["identifier"][],
  ) => {
    setSelectedBranchIds(limitedBranches);
    BranchLimitsHolder.setSelectedBranchIds(limitedBranches);
  };

  // Fetch branch limits on load
  useEffect(() => {
    BranchLimitsHolder.fetchBranchLimits().then(
      ({ branchLimits, isConfigLimited }) => {
        setBranchLimits(branchLimits);

        setIsConfigLimited(isConfigLimited);
      },
    );
    // TODO stop using a subscribe pattern here
    const unsubscribe = BranchLimitsHolder.subscribe(
      ({ selectedBranchIds: selectedIds }) =>
        setSelectedBranchIds([...selectedIds]),
    );
    return () => {
      // cleanup
      unsubscribe();
    };
  }, []);

  // Listen for Logout and clear current limits
  useEffect(() => {
    const handleLogout = () => {
      // Clear current limits
      // Example: setBranchLimits(null);
    };

    // Subscribe to logout event
    // Example: subscribeToEvent('logout', handleLogout);

    // Unsubscribe on component unmount
    return () => {
      // Unsubscribe from logout event
      // Example: unsubscribeFromEvent('logout', handleLogout);
    };
  }, []);

  // Listen for search and update branch filters if necessary
  useEffect(() => {
    // const handleSearch = (searchParams) => {
    // Update branch filters if necessary from search params
    // Example: updateBranchFilters(searchParams);
    // };

    // Subscribe to search event
    // Example: subscribeToEvent('search', handleSearch);

    // Unsubscribe on component unmount
    return () => {
      // Unsubscribe from search event
      // Example: unsubscribeFromEvent('search', handleSearch);
    };
  }, []);

  // Define the context value
  // TODO memoize members and context object
  // potentially split less stable data like selectedBranches from the rest
  const contextValue = {
    branchLimits,
    selectedBranchIds,
    selectedBranches,
    visibleBranches,
    visibleBranchIds,
    displayInCopiesBranchIds,
    displayInPickupLocationIds,
    displayInBranchLimitsIds,
    configLimitedBranches,
    configLimitedBranchIds,
    limitedBranchIds,
    singleCopiesBranchId,
    isConfigLimited,
    updateSelectedBranches,
    displayBranchLimitControl,
  };

  return (
    <BranchLimitContext.Provider value={contextValue}>
      {children}
    </BranchLimitContext.Provider>
  );
};
